import axios from 'axios';
import { apiMainUrl } from '../apiConfig';
import { normalizeUserPermissionSet } from '../Models/SecurityModels';

const API_BASE_URL = apiMainUrl + 'users'; // Adjust the URL based on your actual API's URL

const authConfig = accessToken => ({
    headers: {
        Authorization: `Bearer ${accessToken}`
    }
});

const normalizeUser = user => {
    if (!user) return user;

    const normalized = {
        ...user,
        userId: user.userId ?? user.UserId,
        entraObjectId: user.entraObjectId ?? user.EntraObjectId ?? null,
        email: user.email ?? user.Email ?? '',
        displayName: user.displayName ?? user.DisplayName ?? user.name ?? null,
        isActive: user.isActive ?? user.IsActive ?? false,
        createdAtUtc: user.createdAtUtc ?? user.CreatedAtUtc,
        updatedAtUtc: user.updatedAtUtc ?? user.UpdatedAtUtc ?? null,
        supplierId: user.supplierId ?? user.SupplierId ?? null,
        roleId: user.roleId ?? user.RoleId ?? null,
        roleName: user.roleName ?? user.RoleName ?? null,
        permissions: user.permissions ?? user.Permissions ?? [],
        executablePermissions: user.executablePermissions ?? user.ExecutablePermissions ?? []
    };

    // Preserve the previous service shape for existing license-management consumers.
    normalized.name = normalized.displayName || normalized.email;
    return normalized;
};

const normalizeUsers = response => {
    const users = Array.isArray(response)
        ? response
        : response?.items ?? response?.data ?? response?.results ?? [];
    return users.map(normalizeUser);
};

const getUsers = async (accessToken, includeInactive = false) => {
    try {
        const response = await axios.get(API_BASE_URL, {
            ...authConfig(accessToken),
            params: { includeInactive }
        });
        return normalizeUsers(response.data);
    } catch (error) {
        console.error('Failed to fetch users:', error);
        throw error; // Re-throw the error for handling by the caller
    }
};

const getUser = async (userId, accessToken) => {
    const response = await axios.get(`${API_BASE_URL}/${userId}`, authConfig(accessToken));
    return normalizeUser(response.data);
};

const getCurrentUser = async accessToken => {
    const response = await axios.get(`${API_BASE_URL}/me`, authConfig(accessToken));
    return normalizeUser(response.data);
};

const createUser = async (user, accessToken) => {
    const response = await axios.post(API_BASE_URL, user, authConfig(accessToken));
    return normalizeUser(response.data);
};

const updateUser = async (userId, user, accessToken) => {
    const response = await axios.put(`${API_BASE_URL}/${userId}`, user, authConfig(accessToken));
    return normalizeUser(response.data);
};

const setUserStatus = async (userId, isActive, accessToken) => {
    const response = await axios.patch(
        `${API_BASE_URL}/${userId}/status`,
        { isActive },
        authConfig(accessToken)
    );
    return normalizeUser(response.data);
};

const getUserPermissions = async (userId, accessToken) => {
    const response = await axios.get(`${API_BASE_URL}/${userId}/permissions`, authConfig(accessToken));
    return normalizeUserPermissionSet(response.data);
};

const replaceUserPermissions = async (userId, permissions, accessToken) => {
    const response = await axios.put(
        `${API_BASE_URL}/${userId}/permissions`,
        { permissions },
        authConfig(accessToken)
    );
    return response.data ? normalizeUserPermissionSet(response.data) : null;
};

const setUserRole = async (userId, roleId, accessToken) => {
    const response = await axios.patch(
        `${API_BASE_URL}/${userId}/role`,
        { roleId },
        authConfig(accessToken)
    );
    return normalizeUser(response.data);
};

const getUserRole = async (accessToken) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/role`, authConfig(accessToken));
        return response.data;
    } catch (error) {
        console.error('Failed to fetch user role:', error);
        throw error; // Re-throw the error for handling by the caller
    }
};

const getSuppliers = async (accessToken) => {
    try {
        const config = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        };
        const response = await axios.get(`${API_BASE_URL}/Suppliers`, config);
        return response.data;
    } catch (error) {
        console.error('Failed to fetch users:', error);
        throw error; // Re-throw the error for handling by the caller
    }
};

export const UsersDataService = {
    getUsers,
    getUser,
    getCurrentUser,
    createUser,
    updateUser,
    setUserStatus,
    getUserPermissions,
    replaceUserPermissions,
    setUserRole,
    getSuppliers,
    getUserRole
};
